import { useEffect, useRef } from 'react'

import type { CreatorIntegrationsApi } from '@fourthwall/services/dist/CreatorIntegrationsApiClient'

import { useKlaviyoConfigurationQuery } from '../queries/useKlaviyoConfigurationQuery'
import { useKlaviyoCreateSyncMutation } from '../queries/useKlaviyoCreateSyncMutation'
import { useKlaviyoSyncStatusQuery } from '../queries/useKlaviyoSyncStatusQuery'
import { useRefreshKlaviyoListsMutation } from '../queries/useRefreshKlaviyoListsMutation'
import { useSetKlaviyoListsMutation } from '../queries/useSetKlaviyoListsMutation'

export const useKlaviyo = () => {
  const { klaviyoConfigurationQuery, invalidateKlaviyoConfigurationQuery } =
    useKlaviyoConfigurationQuery()
  const { klaviyoSyncStatusQuery, invalidateKlaviyoSyncStatusQuery } = useKlaviyoSyncStatusQuery()
  const { createKlaviyoSyncMutation } = useKlaviyoCreateSyncMutation()
  const { refreshKlaviyoListsMutation } = useRefreshKlaviyoListsMutation()
  const { setKlaviyoListsMutation } = useSetKlaviyoListsMutation()
  const pollRef = useRef<NodeJS.Timeout | null>(null)

  const refreshLists = async () => {
    try {
      await refreshKlaviyoListsMutation.mutateAsync([])
      invalidateKlaviyoConfigurationQuery()
    } catch (error) {
      console.error('Failed to refresh Klaviyo lists:', error)
      throw error
    }
  }

  const setLists = async (requestBody: CreatorIntegrationsApi.SetKlaviyoLists.RequestBody) => {
    try {
      await setKlaviyoListsMutation.mutateAsync([requestBody])
      invalidateKlaviyoConfigurationQuery()
    } catch (error) {
      console.error('Failed to set Klaviyo lists:', error)
      throw error
    }
  }

  const createSyncJob = async (
    requestBody: CreatorIntegrationsApi.CreateKlaviyoSyncJob.RequestBody,
  ) => {
    try {
      await createKlaviyoSyncMutation.mutateAsync([requestBody])
    } catch (error) {
      console.error('Failed to create Klaviyo sync job:', error)
      throw error
    }

    invalidateKlaviyoSyncStatusQuery()
  }

  useEffect(() => {
    if (klaviyoSyncStatusQuery.data?.activeSync) {
      pollRef.current = setInterval(() => {
        invalidateKlaviyoSyncStatusQuery()
      }, 1000)
    } else if (pollRef.current) {
      clearInterval(pollRef.current)
    }
  }, [klaviyoSyncStatusQuery.data?.activeSync])

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
      }
    }
  }, [])

  const progress = [
    klaviyoSyncStatusQuery.data?.members
      ? { key: 'MEMBERS' as const, ...klaviyoSyncStatusQuery.data.members }
      : null,
    klaviyoSyncStatusQuery.data?.newsletter
      ? { key: 'NEWSLETTER' as const, ...klaviyoSyncStatusQuery.data.newsletter }
      : null,
    klaviyoSyncStatusQuery.data?.offers
      ? { key: 'PRODUCTS' as const, ...klaviyoSyncStatusQuery.data.offers }
      : null,
    klaviyoSyncStatusQuery.data?.promotions
      ? { key: 'PROMOTIONS' as const, ...klaviyoSyncStatusQuery.data.promotions }
      : null,
    klaviyoSyncStatusQuery.data?.collections
      ? { key: 'COLLECTIONS' as const, ...klaviyoSyncStatusQuery.data.collections }
      : null,
  ]
    .filter((item): item is NonNullable<typeof item> => item != null && item.total != null)
    .map((item) => ({
      key: item.key,
      progress: item.progress ?? 0,
      total: item.total,
    }))

  return {
    klaviyo: {
      configuration: klaviyoConfigurationQuery.data,
      isLoading: klaviyoConfigurationQuery.isLoading,
    },
    syncStatus: {
      active: klaviyoSyncStatusQuery.data?.activeSync || false,
      recent: klaviyoSyncStatusQuery.data?.recentSync || false,
      hasSynced: klaviyoSyncStatusQuery.data?.hasSync || false,
      progress,
      isLoading: klaviyoSyncStatusQuery.isLoading,
    },
    refreshLists,
    isRefreshing: refreshKlaviyoListsMutation.isLoading,
    setLists,
    isSettingLists: setKlaviyoListsMutation.isLoading,
    createSyncJob,
    isCreatingSyncJob: createKlaviyoSyncMutation.isLoading,
  }
}
